import React, { useState } from 'react'
import { Card, Button, Row, Col, Toast } from 'react-bootstrap'
import { Deployment } from '../../types/models/model'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy, faCheckCircle, faCode, faKey } from '@fortawesome/free-solid-svg-icons'
import { translateCurl, supportedLanguages } from '../../utils/curlTranslator'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { vs2015 } from 'react-syntax-highlighter/dist/cjs/styles/hljs'
import Select, { SingleValue, StylesConfig } from 'react-select'
import styled from 'styled-components'

interface CurlEndpointCardProps {
  deployment: Deployment | undefined
}

interface LanguageOption {
  value: string
  label: string
  syntax: string
}

const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 24px;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 8px 16px 16px;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const StepCard = styled(Card)`
  background: #1a1a1a;
  border: none;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const StepHeader = styled.div<{ $gradient: string }>`
  background: ${(props) => props.$gradient};
  padding: 1rem;
  border-radius: 8px 8px 0 0;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;

  .step-number {
    background: rgba(255, 255, 255, 0.2);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
  }
`

const StepContent = styled.div`
  background: #2a2a2a;
  padding: 1.5rem;
  border-radius: 0 0 8px 8px;
  height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  p {
    color: #a0a0a0;
    margin-bottom: 1rem;
    flex-shrink: 0;
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #5a5a5a;
  }
`

const StepsRow = styled(Row)`
  margin: 0 -1rem;
  margin-bottom: 2rem;
  min-height: 600px;
  > div {
    padding: 0 1rem;
    height: 100%;
    display: flex;
  }
`

const AuthContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ValueBox = styled.div`
  background: #1a1a1a;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  .value-content {
    flex: 1;
    min-width: 0;

    .label {
      color: #a0a0a0;
      font-size: 0.85rem;
      margin-bottom: 0.25rem;
    }

    .value {
      color: #ffffff;
      font-family: monospace;
      font-size: 0.9rem;
      word-break: break-all;
    }
  }

  .copy-btn {
    flex-shrink: 0;
  }
`

const CodeExample = styled.div`
  background: #1a1a1a;
  border-radius: 6px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;

  .code-header {
    background: #2f2f2f;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #3a3a3a;
    flex-shrink: 0;
    position: relative;
    z-index: 20;

    span {
      color: #ffffff;
      font-weight: 500;
    }

    .language-select {
      width: 150px;
    }
  }

  .code-content {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    pre {
      margin: 0 !important;
      padding: 1.5rem !important;
      padding-top: 3.5rem !important;
      background: transparent !important;
      height: 100% !important;
      overflow-y: auto !important;
      white-space: pre-wrap !important;
      word-wrap: break-word !important;

      code {
        white-space: pre-wrap !important;
        word-wrap: break-word !important;
      }
    }

    .copy-button {
      position: absolute;
      top: 1rem;
      right: 1rem;
      z-index: 10;
      background: #2f2f2f;
      border-color: #3a3a3a;

      &:hover {
        background: #3a3a3a;
        border-color: #4a4a4a;
      }

      &:active {
        background: #4a4a4a !important;
        border-color: #5a5a5a !important;
      }
    }
  }
`

const selectStyles: StylesConfig<LanguageOption> = {
  control: (base) => ({
    ...base,
    backgroundColor: '#2a2a2a',
    borderColor: '#3a3a3a',
    '&:hover': {
      borderColor: '#005cfa',
    },
    boxShadow: 'none',
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#2a2a2a',
    border: '1px solid #3a3a3a',
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? '#005cfa' : isFocused ? '#3a3a3a' : '#2a2a2a',
    color: '#ffffff',
    cursor: 'pointer',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#ffffff',
  }),
}

const CurlEndpointCard: React.FC<CurlEndpointCardProps> = ({ deployment }) => {
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(supportedLanguages[0])

  const gradients = React.useMemo(
    () => ({
      auth: `linear-gradient(${Math.floor(Math.random() * 360)}deg, #402563, #114ee0)`,
      code: `linear-gradient(${Math.floor(Math.random() * 360)}deg, #402563, #2042ba)`,
    }),
    []
  )

  const handleCopyClick = () => {
    if (!deployment) return
    const code = translateCurl(deployment.endpoint_curl, selectedLanguage.value)
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setToastMessage('Code copied to clipboard!')
        setShowToast(true)
      })
      .catch((err) => console.error('Failed to copy:', err))
  }

  const handleCopyValue = (value: string, label: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setToastMessage(`${label} copied to clipboard!`)
        setShowToast(true)
      })
      .catch((err) => console.error('Failed to copy:', err))
  }

  const handleLanguageChange = (newValue: SingleValue<LanguageOption>) => {
    setSelectedLanguage(newValue || supportedLanguages[0])
  }

  // Extract endpoint URL and headers from curl command
  const urlMatch = deployment?.endpoint_curl?.match(/POST ['"]([^'"]*)['"]/)
  let url = urlMatch ? urlMatch[1] : ''

  // Replace development domain with production domain
  url = url.replace('dev.emeraldai-dev.com', 'go-prod.agileview-ai.com')

  const headers: Record<string, string> = {}
  // Updated regex to handle more header formats
  const headerPattern =
    /-H ['"]([^:]+):\s*([^'"]+)['"]|-H "([^:]+):\s*([^"]+)"|--header ['"]([^:]+):\s*([^'"]+)['"]|--header "([^:]+):\s*([^"]+)"/g
  let match
  while ((match = headerPattern.exec(deployment?.endpoint_curl || '')) !== null) {
    // match groups: [full match, -H single quote key, value, -H double quote key, value, --header single quote key, value, --header double quote key, value]
    const key = match[1] || match[3] || match[5] || match[7]
    const value = match[2] || match[4] || match[6] || match[8]
    // Skip Content-Type header
    if (key && value && key !== 'Content-Type') {
      headers[key] = value
    }
  }

  if (!deployment?.endpoint_curl) return null

  return (
    <ViewContainer>
      <ScrollableContent>
        {/* Row 1: Authentication & Endpoint Information */}
        <StepsRow>
          <Col md={5}>
            <StepCard>
              <StepHeader $gradient={gradients.auth}>
                <span className="step-number">1</span>
                Authentication & Endpoint
                <FontAwesomeIcon icon={faKey} />
              </StepHeader>
              <StepContent>
                <p>You&apos;ll need these credentials and endpoint to make API requests:</p>
                <AuthContent>
                  <ValueBox>
                    <div className="value-content">
                      <div className="label">Endpoint URL</div>
                      <div className="value">{url}</div>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="copy-btn"
                      onClick={() => handleCopyValue(url, 'Endpoint URL')}
                    >
                      <FontAwesomeIcon icon={faCopy} className="me-2" />
                      Copy
                    </Button>
                  </ValueBox>
                  {Object.entries(headers).map(([key, value]) => (
                    <ValueBox key={key}>
                      <div className="value-content">
                        <div className="label">{key}</div>
                        <div className="value">{value}</div>
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="copy-btn"
                        onClick={() => handleCopyValue(value, key)}
                      >
                        <FontAwesomeIcon icon={faCopy} className="me-2" />
                        Copy
                      </Button>
                    </ValueBox>
                  ))}
                </AuthContent>
              </StepContent>
            </StepCard>
          </Col>

          <Col md={7}>
            {/* Code Example */}
            <StepCard>
              <StepHeader $gradient={gradients.code}>
                <span className="step-number">2</span>
                Code Example
                <FontAwesomeIcon icon={faCode} />
              </StepHeader>
              <StepContent>
                <p>
                  Here&apos;s a complete example showing how to send an image and get predictions:
                </p>
                <CodeExample>
                  <div className="code-header">
                    <span>Select your programming language:</span>
                    <Select<LanguageOption>
                      className="language-select"
                      classNamePrefix="select"
                      options={supportedLanguages}
                      value={selectedLanguage}
                      onChange={handleLanguageChange}
                      styles={selectStyles}
                      isSearchable={false}
                    />
                  </div>
                  <div className="code-content">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCopyClick}
                      className="copy-button"
                    >
                      <FontAwesomeIcon icon={faCopy} className="me-2" />
                      Copy Code
                    </Button>
                    <SyntaxHighlighter
                      language={selectedLanguage.syntax}
                      style={vs2015}
                      customStyle={{
                        fontSize: '0.9em',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                      wrapLongLines={true}
                    >
                      {translateCurl(deployment?.endpoint_curl || '', selectedLanguage.value)}
                    </SyntaxHighlighter>
                  </div>
                </CodeExample>
              </StepContent>
            </StepCard>
          </Col>
        </StepsRow>
      </ScrollableContent>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={2000}
        autohide
        className="position-fixed top-0 end-0 m-4"
        style={{ zIndex: 1000 }}
      >
        <Toast.Body className="d-flex align-items-center bg-success text-white">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          {toastMessage}
        </Toast.Body>
      </Toast>
    </ViewContainer>
  )
}

export default CurlEndpointCard
